import React from 'react';
import { sectionFadeInStyle, GREY_PLACEHOLDER_SVG } from './utils';
import type { PdpProductCardStyles } from './pdpProductInfoCardConfig';

const formatReviewCount = (count: number): string => {
  if (count >= 1000) {
    const k = count / 1000;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return count.toLocaleString();
};

const StarIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.4819 6.07562C15.4285 5.8977 15.2864 5.76804 15.1153 5.74117L10.3089 4.98558L8.1594 0.273902C8.0829 0.106169 7.92499 0 7.75211 0C7.5792 0 7.42132 0.106169 7.3448 0.273902L5.19518 4.98558L0.388901 5.74117C0.217838 5.76804 0.0756731 5.89771 0.0222526 6.07558C-0.0311937 6.2535 0.0133808 6.44878 0.137199 6.57932L3.61497 10.2469L2.7941 15.4256C2.76485 15.6099 2.83492 15.7962 2.97479 15.9062C3.05391 15.9684 3.14764 16 3.24182 16C3.31413 16 3.38668 15.9814 3.45312 15.9436L7.75208 13.4985L12.0509 15.9435C12.204 16.0306 12.3894 16.0161 12.5292 15.9061C12.6691 15.7962 12.7392 15.6099 12.71 15.4255L11.8888 10.2469L15.367 6.5793C15.4908 6.44879 15.5354 6.2535 15.4819 6.07562H15.4819Z"
      fill={color}
    />
  </svg>
);

export interface PdpProductInfoCardProps {
  title: string;
  imageUrl?: string;
  rating?: number;
  reviewsCount?: number;
  /** Resolved per-catalog by the V1/V2 part. When empty, the card is not a link. */
  pdpUrl?: string;
  /** Resolved style values — theme defaults merged with any brand overrides. */
  styles: PdpProductCardStyles;
}

/**
 * The card's outer box. It is an `<a>` when a pdp url resolved and a plain
 * `<div>` otherwise — an anchor with no href is not focusable and reads as a
 * broken link to a screen reader.
 */
function CardShell({
  pdpUrl,
  styles,
  children,
}: {
  pdpUrl?: string;
  styles: PdpProductCardStyles;
  children: React.ReactNode;
}) {
  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: styles.containerPadding,
    border: `1px solid ${styles.containerBorderColor}`,
    borderRadius: styles.containerBorderRadius,
    gap: 16,
    minHeight: 80,
    boxSizing: 'border-box',
    textDecoration: 'none',
    color: 'inherit',
  };
  if (!pdpUrl) return <div style={style}>{children}</div>;
  return (
    <a href={pdpUrl} target="_blank" rel="noopener noreferrer" style={{ ...style, cursor: 'pointer' }}>
      {children}
    </a>
  );
}

/**
 * Presentational PDP product card (image, title, rating). Catalog and URL
 * resolution live in the V1/V2 parts; editable styling is resolved by
 * `PdpProductInfoPart`. This component only renders.
 *
 * The card carries no button: every action lives in the product detail sidebar,
 * so the WHOLE card is the link to `pdpUrl` instead of a "View" CTA.
 */
export function PdpProductInfoCard({ title, imageUrl, rating, reviewsCount, pdpUrl, styles }: PdpProductInfoCardProps) {
  return (
    <div style={{ animation: 'concierge-fade-in 0.35s ease-out both' }}>
      <style dangerouslySetInnerHTML={{ __html: sectionFadeInStyle }} />
      <CardShell pdpUrl={pdpUrl} styles={styles}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
          <img
            src={imageUrl || GREY_PLACEHOLDER_SVG}
            alt={title}
            style={{
              width: styles.imageSize,
              height: styles.imageSize,
              objectFit: 'contain',
              flexShrink: 0,
              alignSelf: 'flex-start',
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 16,
              rowGap: 2,
              minWidth: 0,
              flex: 1,
            }}
          >
            <span
              style={{
                fontFamily: 'inherit',
                fontWeight: styles.titleFontWeight,
                fontSize: styles.titleFontSize,
                lineHeight: '24px',
                color: styles.titleColor,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 120,
                maxWidth: 380,
                flex: 1,
              }}
            >
              {title}
            </span>
            {rating != null && rating > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <StarIcon color={styles.ratingStarColor} />
                <span
                  style={{
                    fontFamily: 'inherit',
                    fontWeight: 500,
                    fontSize: styles.ratingFontSize,
                    lineHeight: '16px',
                    color: styles.ratingTextColor,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {rating.toFixed(1)}
                  {reviewsCount != null && reviewsCount > 0 && ` (${formatReviewCount(reviewsCount)} reviews)`}
                </span>
              </span>
            )}
          </div>
        </div>
      </CardShell>
    </div>
  );
}
